import { createFileRoute } from "@tanstack/react-router";
import { CheckSquare, Bell, Users } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroImg from "@/assets/pipelines-tasks-trio.webp";
import workflowImg from "@/assets/pipelines-tasks-create.webp";
import teamImg from "@/assets/pipelines-tasks-board.webp";

export const Route = createFileRoute("/platform/tasks")({
  head: () => ({
    meta: [
      { title: "Tasks — banana.cards" },
      { name: "description", content: "Assign, schedule and complete to-dos for yourself and your team. Linked to contacts, deals and pipelines — never drop a ball." },
      { property: "og:title", content: "Tasks — banana.cards" },
      { property: "og:description", content: "Assign, schedule and complete to-dos for yourself and your team. Linked to contacts, deals and pipelines — never drop a ball." },
      { property: "og:image", content: heroImg },
      { property: "og:url", content: "https://banana.cards/platform/tasks" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/platform/tasks" }],
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Tasks"
      title="Stay on top of"
      italic="everything."
      description="Create, assign and complete tasks for yourself and your team — linked to the contacts, deals and meetings they actually belong to. Reminders that fire when they should."
      heroImage={heroImg}
      heroImageAlt="Task inbox on banana.cards showing tasks attached to contacts, deals and calendar events"
      metrics={[{ v: "Unlimited", l: "tasks" }, { v: "Smart", l: "reminders" }, { v: "Team", l: "assignment" }]}
      bullets={[
        { icon: CheckSquare, title: "Tied to context", desc: "Every task lives on a contact, deal or meeting — open the task, see the why." },
        { icon: Bell, title: "Reminders that fire", desc: "In-app, email, push — choose how Banana nudges you on every task." },
        { icon: Users, title: "Built for teams", desc: "Assign, reassign and watch — full visibility across the team without micromanaging." }
      ]}
      deepDives={[
        { eyebrow: "Workflow", title: "Tasks where", italic: "the work is.", description: "Open a contact and see every task. Open a deal and see every task. Create one from anywhere — calendar, contact, meeting transcript — without context switching.", imageAlt: "Creating a task directly from a contact, deal or meeting transcript on banana.cards", image: workflowImg, reverse: false, points: ["Tasks on contacts & deals", "Recurring tasks", "Sub-tasks & dependencies", "Mobile-first inbox"] },
        { eyebrow: "Team", title: "Coach with", italic: "visibility.", description: "Manager views show open tasks per rep, overdue counts and where work is piling up. Round-robin assignment keeps load balanced.", imageAlt: "Manager task board on banana.cards with round-robin assignment, overdue alerts and SLA tracking", image: teamImg, reverse: true, points: ["Round-robin assignment", "Overdue alerts", "Manager dashboards", "SLA tracking"] }
      ]}
      howItWorks={[
        { title: "Create a task", desc: "From a contact, deal or meeting." },
        { title: "Assign & schedule", desc: "Who, when, how Banana reminds." },
        { title: "Banana nudges", desc: "In-app, email, push." },
        { title: "Done — auto-logged", desc: "Completion shows in the activity feed." }
      ]}
      capabilities={[
        "Unlimited tasks",
        "Linked to contacts & deals",
        "Recurring tasks",
        "Sub-tasks",
        "Round-robin assignment",
        "Manager dashboards",
        "SLA & overdue alerts",
        "Mobile inbox"
      ]}
      faq={[
        { q: "Can I link tasks to a contact, deal or meeting?", a: "Yes — every task can attach to a CRM contact, a pipeline deal card and/or a meeting. That means 'Follow up with Acme on pricing' lives on Acme's contact timeline AND on the Acme deal card AND on your personal task inbox — close it anywhere and it closes everywhere." },
        { q: "Can tasks repeat on a schedule?", a: "Yes — set tasks to recur daily, weekly, monthly or with a custom cadence (e.g. 'every other Tuesday', 'first of every month'). Each occurrence is its own completable task so you keep a clean history of what was actually done." },
        { q: "Will I get reminders for upcoming and overdue tasks?", a: "Yes — Banana groups your tasks into Overdue, Today, Tomorrow, This week and Later, and pushes mobile and email notifications based on your reminder settings. Overdue tasks are visually flagged so they don't get lost." },
        { q: "Can I assign tasks to teammates or round-robin them?", a: "Yes — assign to a specific person, multiple people, or round-robin across a team so workload distributes evenly. Members can see what's on their plate; admins can see the team workload at a glance." },
        { q: "Can tasks be created automatically by other parts of Banana?", a: "Yes — automations can spawn tasks on triggers like 'new contact tagged Lead', 'deal moved to Won', 'meeting finished with action items', or 'contract not signed in 3 days'. So most of your task list builds itself." },
        { q: "Do tasks sync with my calendar?", a: "Yes — tasks with due dates and times can sync as events into Google Calendar, Outlook or Apple iCloud, so they sit next to your meetings." }
      ]}
      outro={{ title: "Never drop", italic: "a follow-up.", description: "Tasks that live where the relationship does — and remind you when it matters." }}
    />
  );
}